import { Link, useParams } from 'react-router-dom';
import { Award, Calendar, ChevronLeft, Clock, ScrollText, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { useFormation, useFormateurs, useSessions } from '@/hooks/queries/useFormation';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import { formatDA, formatDateDZ } from '@/lib/format';
import { t } from '@/locales/i18n';

export function FicheFormationPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: formation } = useFormation(id ?? null);
  const { data: sessions = [] } = useSessions({ formationId: id ?? null });
  const { data: formateurs = [] } = useFormateurs();
  const { data: sousSegments = [] } = useAllSousSegments();

  if (!formation) {
    return (
      <div className="p-6">
        <Link
          to="/formation/catalogue"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
        >
          <ChevronLeft className="h-3 w-3" /> Retour au catalogue
        </Link>
        <div className="mt-4 text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  const formateur = formateurs.find((f) => f.id === formation.formateur_principal_id);
  const sousSegment = sousSegments.find((s) => s.id === formation.sous_segment_id);

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/formation/catalogue"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
      >
        <ChevronLeft className="h-3 w-3" /> Retour au catalogue
      </Link>
      <PageHeader
        title={formation.titre}
        subtitle={
          <span className="inline-flex items-center gap-2 text-xs">
            <span className="font-mono">{formation.code}</span>
            {sousSegment && <Badge variant="outline">{sousSegment.nom}</Badge>}
            <Badge variant="secondary">{formation.niveau}</Badge>
            {formation.certif_delivree && (
              <Badge variant="success">
                <ScrollText className="mr-1 h-2.5 w-2.5" /> {formation.code_certif ?? 'Certifiante'}
              </Badge>
            )}
          </span>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          {formation.description && (
            <section className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 text-sm font-semibold">Description</h3>
              <p className="text-xs text-muted-foreground">{formation.description}</p>
            </section>
          )}
          {formation.objectifs && (
            <section className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 text-sm font-semibold">Objectifs pédagogiques</h3>
              <p className="whitespace-pre-line text-xs text-muted-foreground">
                {formation.objectifs}
              </p>
            </section>
          )}
          {formation.prerequis && (
            <section className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 text-sm font-semibold">Prérequis</h3>
              <p className="text-xs text-muted-foreground">{formation.prerequis}</p>
            </section>
          )}
          {formation.competences.length > 0 && (
            <section className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 text-sm font-semibold">Compétences acquises</h3>
              <ul className="flex flex-wrap gap-1">
                {formation.competences.map((c) => (
                  <li key={c}>
                    <Badge variant="outline">{c}</Badge>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Calendar className="h-4 w-4" /> Sessions programmées ({sessions.length})
            </h3>
            {sessions.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Aucune session programmée pour cette formation.
              </p>
            ) : (
              <ul className="space-y-2">
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/formation/sessions/${s.id}`}
                        className="font-medium hover:underline"
                      >
                        {s.reference} · {t(`formation.session.type.${s.type}`)}
                      </Link>
                      <div className="text-[10px] text-muted-foreground">
                        {formatDateDZ(s.date_debut)} → {formatDateDZ(s.date_fin)}
                      </div>
                    </div>
                    <Badge variant="outline">{t(`formation.session.statut.${s.statut}`)}</Badge>
                    {s.prix_session && (
                      <span className="num text-xs">{formatDA(Number(s.prix_session))}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="space-y-3">
          <div className="rounded-lg border border-border bg-card p-3 text-xs">
            <div className="mb-2 text-sm font-semibold">Détails</div>
            <Row
              label="Durée"
              value={`${formation.duree_jours ?? '—'}j (${formation.duree_heures ?? '—'}h)`}
              icon={<Clock className="h-3 w-3" />}
            />
            <Row label="Modalités" value={formation.modalites.join(', ')} />
            <Row label="Niveau" value={formation.niveau} />
            {formation.prix_inter_unitaire && (
              <Row
                label="Prix inter"
                value={`${formatDA(Number(formation.prix_inter_unitaire))} / pers.`}
              />
            )}
            {formation.prix_intra_forfait && (
              <Row label="Prix intra" value={formatDA(Number(formation.prix_intra_forfait))} />
            )}
            {formation.prix_b2c && (
              <Row label="Prix B2C" value={formatDA(Number(formation.prix_b2c))} />
            )}
            {formation.validite_mois && (
              <Row label="Validité certif" value={`${formation.validite_mois} mois`} />
            )}
          </div>

          {formateur && (
            <div className="rounded-lg border border-border bg-card p-3 text-xs">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <Users className="h-3.5 w-3.5" /> Formateur principal
              </div>
              <div className="font-medium">
                {formateur.prenom} {formateur.nom}
              </div>
              <div className="mt-1 text-muted-foreground">{formateur.bio}</div>
              {formateur.note_moyenne != null && (
                <Badge variant="success" className="mt-2 text-[10px]">
                  <Award className="h-2.5 w-2.5" /> Note moyenne{' '}
                  {Number(formateur.note_moyenne).toFixed(1)} / 5
                </Badge>
              )}
            </div>
          )}

          {formation.taux_satisfaction_moyen != null && (
            <div className="rounded-lg border border-border bg-card p-3 text-xs">
              <div className="mb-1 text-sm font-semibold">Satisfaction</div>
              <div className="num text-2xl font-bold">
                {Number(formation.taux_satisfaction_moyen).toFixed(1)} / 10
              </div>
              <div className="text-[10px] text-muted-foreground">
                NPS moyen sur {formation.nb_evaluations} évaluation(s)
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-right">{value}</span>
    </div>
  );
}
